import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/common";
import ReportFilters, {
  defaultDateRange, formatSec, ReportPagination, REPORT_PAGE_SIZE,
} from "@/components/reports/ReportFilters";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChartBar } from "@phosphor-icons/react";

export default function CampaignDispositionReport() {
  const { user } = useAuth();
  const accountTz = user?.timezone || "UTC";
  const defaults = defaultDateRange(1, accountTz);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [campaignId, setCampaignId] = useState("");
  const [applied, setApplied] = useState({ ...defaults, campaignId: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tz = data?.timezone || accountTz;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: applied.dateFrom,
        date_to: applied.dateTo,
        page,
        page_size: REPORT_PAGE_SIZE,
      };
      if (applied.campaignId) params.campaign_id = applied.campaignId;
      const { data: res } = await api.get("/reports/campaign-disposition", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => { load(); }, [load]);

  const chartData = (data?.campaigns || []).slice(0, 12).map((c) => ({
    name: (c.campaign_name || "—").slice(0, 18),
    calls: c.total,
  }));
  const rowsTotal = data?.rows_total ?? (data?.campaigns || []).length;

  return (
    <div data-testid="campaign-disposition-report">
      <PageHeader
        title="Campaign disposition"
        subtitle={`Disposition totals grouped by campaign, with charts and filters. Days in ${tz}.`}
      />

      <ReportFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        campaignId={campaignId}
        onCampaignId={setCampaignId}
        campaigns={data?.campaign_options || []}
        onApply={() => {
          setPage(1);
          setApplied({ dateFrom, dateTo, campaignId });
        }}
        onClear={() => {
          const d = defaultDateRange(1, accountTz);
          setDateFrom(d.dateFrom); setDateTo(d.dateTo); setCampaignId("");
          setPage(1);
          setApplied({ ...d, campaignId: "" });
        }}
        applying={loading}
        testid="campaign-dispo-filters"
        timezone={tz}
      />

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.total === 0 ? (
        <EmptyState
          icon={ChartBar}
          title="No campaign disposition data"
          description="Try a wider date range."
          testid="campaign-dispo-empty"
        />
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-4 h-72 mb-6">
            <p className="text-sm font-semibold mb-2">Calls by campaign</p>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="calls" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Talk time</TableHead>
                  <TableHead>Top dispositions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.campaigns || []).map((c) => (
                  <TableRow key={c.campaign_id || "none"}>
                    <TableCell className="font-medium text-sm">{c.campaign_name || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.total}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSec(c.talk_sec)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(c.by_disposition || []).slice(0, 4).map((d) => `${d.code} (${d.count})`).join(" · ") || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ReportPagination
              page={data.page || page}
              pageSize={data.page_size || REPORT_PAGE_SIZE}
              total={rowsTotal}
              onPage={setPage}
              testid="campaign-dispo-pagination"
            />
          </div>
        </>
      )}
    </div>
  );
}
